import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const WestEnd = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-end-vancouver:About West End Paragraph">
          Living in the West End,{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">BC</LinkText>{' '}
          offers a distinct and captivating experience with its vibrant
          atmosphere, diverse communities, and abundant entertainment options.
          Convenience is a major perk, as everything you need is just a short
          walk away, with excellent public transportation readily available.{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighbourhood-guide-west-end-vancouver/">
            The West End
          </LinkText>{' '}
          boasts world-class shopping, dining, and entertainment venues,
          including the iconic{' '}
          <LinkText href="https://www.destinationvancouver.com/vancouver/neighbourhoods/robson-street/">
            Robson Street
          </LinkText>{' '}
          and a plethora of restaurants. Each neighborhood within the West End
          has its own unique charm, from the historic allure of{' '}
          <LinkText href="https://www.destinationvancouver.com/vancouver/neighbourhoods/davie-village/">
            Davie Village
          </LinkText>{' '}
          to the trendy ambiance of{' '}
          <LinkText href="https://www.tripadvisor.com/Attraction_Review-g154943-d555072-Reviews-Denman_Street-Vancouver_British_Columbia.html">
            Denman Street
          </LinkText>
          . Despite being in the heart of the city, the West End is surrounded
          by natural beauty, with opportunities for outdoor activities like
          exploring the nearby{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/stanley-park.aspx">
            Stanley Park
          </LinkText>{' '}
          or enjoying the picturesque seawall. Outdoor enthusiasts will also
          appreciate the proximity to hiking trails, beaches, and ski resorts.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,500 - $3,400',
          bed2: '$3,500 - $4,900',
          bed3: '$4,200 - $5,300'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.west-end-vancouver:Apartment Hunting West End Paragraph 1">
            Finding an apartment for rent in West End, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant West End lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.west-end-vancouver:Apartment Hunting West End Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Apartments for rent in West End Paragraph">
              The West End neighborhood in Vancouver, BC, offers a wide range of{' '}
              <LinkText href={links.apartment}>apartments for rent</LinkText>{' '}
              catered to individuals and young professionals seeking a
              convenient and hassle-free living experience. With an array of
              sizes and designs to choose from, residents can easily find their
              ideal space. From contemporary high-rise buildings with sleek
              aesthetics to quaint heritage apartments, the West End has
              something to offer for everyone.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Houses for rent in West End Paragraph">
              The{' '}
              <LinkText href={links.house}>
                houses for rent in Vancouver’s West End
              </LinkText>{' '}
              neighborhood offer an exceptional living experience in one of the
              city’s most coveted areas. With its pristine outdoor spaces and a
              blend of historic residences and contemporary architectural
              marvels, this locale is perfect for families and individuals
              seeking privacy and charm. Enjoy the unique character of the area
              while being in close proximity to various amenities and
              attractions. Whether you desire a serene family home or a lively
              urban retreat, the West End’s rental houses strike a harmonious
              balance between tranquility and convenience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Townhouses for rent in West End Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in the West End, Vancouver, BC
              </LinkText>
              , offer a harmonious blend of communal living and personal
              privacy. These multi-level homes with small outdoor spaces are
              ideal for individuals seeking extra room to relax. Townhouse
              living provides the perfect balance, enabling residents to enjoy
              the advantages of a tight-knit community while also having their
              own secluded space. Whether you’re in search of rental townhouses
              in the West End of Vancouver or considering purchasing one, these
              properties present a distinct opportunity to immerse yourself in
              the vibrant city while enjoying a cozy and expansive abode.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Basements for rent in West End Paragraph">
              Basements for rent in the West End, Vancouver, BC, are a
              cost-effective housing option for singles or students seeking
              affordable accommodations. These basement suites in the West End
              often come with a separate entrance and basic amenities, making
              them a practical choice for budget-conscious individuals. Although
              basement suites in the West End may not offer abundant natural
              light like other housing options, they do provide a cozy and
              private living space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Condos for rent West End Paragraph">
              The West End, a bustling neighborhood in Vancouver, BC, is
              renowned for its convenience, strong sense of community, and
              hassle-free living. What sets this area apart is its extensive
              collection of available condos for rent, offering an array of
              enticing features like fitness centers, rooftop gardens, and
              concierge services.{' '}
              <LinkText href={links.apartment}>
                Renting a condo in the West End
              </LinkText>{' '}
              not only allows residents to fully immerse themselves in the
              neighborhood’s vibrant energy but also provides them with all the
              comforts of home. Discover the perfect West End condo for rent and
              experience the best of both worlds.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Rooms for rent in West End Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in the West End, Vancouver, BC
              </LinkText>{' '}
              offer a convenient and cost-effective housing solution. Whether
              you’re a student seeking a shared living space or someone who
              appreciates the communal aspect of living, renting a room in this
              vibrant neighborhood allows you to reside in the heart of the city
              while keeping expenses in check. With a welcoming ambiance and
              ample opportunities to connect with fellow residents, renting a
              room in the West End provides an excellent opportunity to explore
              and enjoy Downtown Vancouver without breaking the bank.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.west-end-vancouver:Short term rentals in West End Paragraph">
              West End Vancouver, known for its residential character and
              proximity to downtown amenities, attracts a mix of residents and
              visitors. While it may not be a hub for employment opportunities
              and academic institutions like the broader downtown area, its
              appeal lies in its vibrant community, scenic beaches, and Stanley
              Park.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              popular, particularly during the summer months when tourists flock
              to the area for its natural beauty and urban conveniences. The
              demand from tourists, along with locals seeking temporary stays,
              makes short-term rentals a practical option in this area. The
              area’s unique blend of urban living and natural surroundings,
              coupled with the{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                flexibility of short-term rentals
              </LinkText>
              , makes it an attractive location for those looking for
              accommodation in a dynamic yet serene part of Vancouver.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-end-vancouver:Exploring West End Paragraph">
          The West End is a captivating neighborhood that offers a unique
          experience for those exploring{' '}
          <LinkText href="https://vancouver.ca/news-calendar/downtown.aspx">
            Downtown Vancouver
          </LinkText>
          , British Columbia. Nestled in the heart of the city, this diverse and
          lively area is characterized by its towering residential buildings,
          charming parks, and close proximity to the iconic{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/stanley-park.aspx">
            Stanley Park
          </LinkText>
          . With its vibrant atmosphere and bustling streets, the West End is a
          must-visit destination for art enthusiasts, trendsetters, and anyone
          looking to immerse themselves in the rich tapestry of Vancouver’s
          cultural scene.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-end-vancouver:Things To Do West End Paragraph">
          The West End neighborhood of Vancouver, BC, is a treasure trove of
          exciting activities and attractions for visitors to enjoy. Nature
          enthusiasts can immerse themselves in the beauty of Stanley Park, a
          sprawling urban oasis boasting scenic trails, a seawall perfect for
          biking and walking, and even the renowned{' '}
          <LinkText href="https://www.vanaqua.org/">
            Vancouver Aquarium
          </LinkText>
          . Art lovers will find their bliss at the{' '}
          <LinkText href="https://www.vanartgallery.bc.ca/">
            Vancouver Art Gallery
          </LinkText>
          , a hub of contemporary and historical artworks that never fails to
          inspire. Food connoisseurs can embark on a culinary adventure at{' '}
          <LinkText href="https://granvilleisland.com/">
            Granville Island
          </LinkText>
          , where the vibrant food scene offers a delightful array of local
          produce, seafood, and artisanal goods at the Public Market. For those
          with a penchant for shopping,{' '}
          <LinkText href="https://www.destinationvancouver.com/vancouver/neighbourhoods/robson-street/">
            Robson Street
          </LinkText>{' '}
          is a dream come true, with its high-end boutiques and international
          brands. And let’s not forget the picturesque waterfront of{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/neighborhood-guide-coal-harbour/">
            Coal Harbour
          </LinkText>
          , perfect for leisurely strolls, or the scenic Aquabus ride to
          Granville Island. With its diverse range of activities, stunning
          natural beauty, and rich cultural offerings, West End is a must-visit
          destination that caters to both locals and tourists alike.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.west-end-vancouver:Restaurants West End Paragraph">
        <Paragraph>
          West End Vancouver is renowned for its vibrant food scene, offering a
          plethora of dining options to satisfy every palate. Here are the top
          five restaurants that should not be missed when exploring the culinary
          delights of West End Vancouver:
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.tripadvisor.com/Restaurant_Review-g154943-d8651977-Reviews-Bistro_Verde-Vancouver_British_Columbia.html">
            Bistro Verde
          </LinkText>{' '}
          - A charming eatery that offers a delightful fusion of Mediterranean
          and West Coast flavors. Led by talented chefs, Bistro Verde showcases
          locally sourced ingredients to create dishes that are both flavorful
          and visually stunning.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://zakkushi.com/">
            Zakkushi Charcoal Grill
          </LinkText>{' '}
          - A cozy spot specializing in yakitori, a traditional Japanese
          skewered grilled cuisine. With its cozy atmosphere and mouthwatering
          menu, Zakkushi Charcoal Grill is a haven for food enthusiasts who
          appreciate the art of grilling.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://theflyingpigvan.com/">
            The Flying Pig
          </LinkText>{' '}
          - A popular restaurant with a cozy and rustic ambiance, offering an
          array of hearty and satisfying dishes that are sure to please any
          palate.
        </Paragraph>
        <Paragraph>
          4) <LinkText href="https://thenaam.com/">The Naam</LinkText> - A
          beloved vegetarian and vegan restaurant that has been serving
          delicious food for over 50 years. They offer a wide range of dishes,
          including hearty burgers, nutritious bowls, and delectable desserts.
        </Paragraph>
        <Paragraph>
          5){' '}
          <LinkText href="https://m.yelp.com/biz/green-leaf-sushi-vancouver">
            The Green Leaf
          </LinkText>{' '}
          - A vegetarian and vegan-friendly restaurant that focuses on fresh,
          organic ingredients. The Green Leaf offers a diverse menu of flavorful
          plant-based dishes that are as satisfying as they are nutritious.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-end-vancouver:Transportation West End Paragraph">
          Transportation options in the West End of Vancouver, BC are diverse
          and accessible. The neighborhood is well-served by the city’s
          comprehensive{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , which includes buses,{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , and{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/seabus">
            SeaBus
          </LinkText>
          . These options allow residents and visitors to conveniently explore
          different parts of the West End and beyond. For those who prefer
          cycling, the area offers a network of bike lanes and rental services,
          making it easy to navigate on two wheels. Moreover, the West End is
          known for its pedestrian-friendly streets, with many attractions and
          amenities within walking distance. If driving is preferred, there are
          various parking options available, such as street parking and parking
          garages. Overall, the West End of Vancouver provides a range of
          transportation choices to cater to the needs of all travelers.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.west-end-vancouver:Where To Stay West End Paragraph">
          When it comes to finding the perfect place to stay in Vancouver’s West
          End neighborhood, there are plenty of accommodation options to suit
          every traveler’s needs and preferences. Whether you’re looking for
          luxury or affordability, West End has got you covered. For those
          seeking a lavish experience, renowned hotels like the{' '}
          <LinkText href="https://www.fairmont.com/pacific-rim-vancouver/">
            Fairmont Pacific Rim
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.shangri-la.com/">
            Shangri-La Hotel
          </LinkText>{' '}
          offer breathtaking views, exceptional service, and top-notch
          amenities. On the other hand, budget-conscious travelers can find
          comfort and affordability at hotels such as the{' '}
          <LinkText href="https://www.wyndhamhotels.com/days-inn/vancouver-british-columbia/days-inn-vancouver-downtown/overview">
            Days Inn by Wyndham Vancouver Downtown
          </LinkText>
          . Additionally, West End provides various short-term rental options,
          including <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>,
          where you can rent anything from a spare room to an entire apartment
          or house. With an array of choices, from cozy apartments to luxurious
          penthouses, West End is the ideal location to stay while exploring the
          vibrant city of Vancouver.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default WestEnd
